import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { User } from "./UserTab";
import {
  commonButtonProps,
  commonTextFieldProps,
} from "../../../../utils/styles";
import { UserScope } from "../../../../types/operationType";
import { useToast } from "../../../../components/Toast";
import { getValidToken } from "../../../../utils/authUtils";
import axios from "axios";
import { getPermissionSummary } from "../../../../utils/permissionUtils";
import { formatText } from "../../../../utils/utils";
import { PlanStatus } from "../../../../utils/alertUtils";

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editUser?: User;
  userScope: UserScope;
  planStatus: PlanStatus;
}

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9+\-\s()]*$/, "Must be a number")
    .min(10, "Must be exactly 10 digits")
    .max(10, "Must be exactly 10 digits"),
  role: Yup.string()
    .required("Role is required")
    .notOneOf(["Select"], "Role is required"),
  email: Yup.string().email("Invalid email format").optional(),
  // permissions: Yup.array()
  //   .of(Yup.string())
  //   .min(1, "At least one permission must be selected"),
});

const initialValues: User = {
  name: "",
  role: "Select",
  phoneNumber: "",
  email: "",
};

const ROLES = [
  "OWNER",
  "MANAGER",
  // "SUPERVISOR",
  "STAFF",
  // "FINANCE",
  // "MAINTENANCE",
];

// Role hierarchy - higher index means higher priority
const ROLE_HIERARCHY = {
  OWNER: 3,
  MANAGER: 2,
  STAFF: 1,
  SUPERVISOR: 1,
};

// Get available roles based on current user's role
const getAvailableRoles = (currentUserRole: string): string[] => {
  const currentUserPriority =
    ROLE_HIERARCHY[currentUserRole as keyof typeof ROLE_HIERARCHY];

  if (!currentUserPriority) {
    return []; // If current user role is not in hierarchy, no roles available
  }

  // Filter roles that have lower priority than current user (not equal)
  // This prevents users from assigning roles at a higher level
  return ROLES.filter((role) => {
    const rolePriority = ROLE_HIERARCHY[role as keyof typeof ROLE_HIERARCHY];
    return rolePriority && rolePriority <= currentUserPriority;
  });
};

// Check if user can assign the given role
export const canAssignRole = (
  currentUserRole: string,
  targetRole: string
): boolean => {
  const currentUserPriority =
    ROLE_HIERARCHY[currentUserRole as keyof typeof ROLE_HIERARCHY];
  const targetRolePriority =
    ROLE_HIERARCHY[targetRole as keyof typeof ROLE_HIERARCHY];

  if (!currentUserPriority || !targetRolePriority) {
    return false;
  }

  return targetRolePriority <= currentUserPriority;
};

const addUser = async (data: any, token: string) => {
  try {
    const enrichedData = {
      name: data?.name,
      phoneNumber: data?.phoneNumber,
      email: data?.email,
      companyId: data?.companyId,
      role: data?.role,
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST}auth/signup`,
      enrichedData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.isSuccess) {
      return response.data.data;
    }
    // Handle case where isSuccess is false but no exception was thrown
    if (response.data.message) {
      throw new Error(response.data.message);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    return [];
  } catch (error: any) {
    console.error("Error adding user:", error);
    // Extract error message from axios error response
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    // If it's already an Error object, re-throw it
    if (error instanceof Error) {
      throw error;
    }
    // Fallback error message
    throw new Error("Failed to add user");
  }
};

const updateUser = async (data: any, token: string, companyId: string) => {
  try {
    const enrichedData = {
      id: data?.id,
      name: data?.name,
      phoneNumber: data?.phoneNumber,
      email: data?.email,
      companyId: data?.companyId,
      role: data?.role,
    };
    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}users/company/${companyId}/${data?.id}`,
      enrichedData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data?.isSuccess) {
      return response.data?.data;
    } else {
      // Handle case where isSuccess is false but no exception was thrown
      if (response.data.message) {
        throw new Error(response.data.message);
      }
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      throw new Error("Failed to update user");
    }
  } catch (error: any) {
    console.error("Error updating user:", error);
    // Extract error message from axios error response
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    // If it's already an Error object, re-throw it
    if (error instanceof Error) {
      throw error;
    }
    // Fallback error message
    throw new Error("Failed to update user");
  }
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  onSave,
  editUser,
  userScope,
  planStatus,
}) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [permissionSummary, setPermissionSummary] = useState<any>();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const currentUserRole = planStatus?.user?.userRole?.roleCode;

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const matrixData = await getPermissionSummary();
        if (matrixData) {
          setPermissionSummary(matrixData);
        }
      } catch (error) {
        console.error("Failed to fetch permission matrix:", error);
      }
    };

    if (open) {
      fetchPermissions();
    }
  }, [open]);

  const handleSaveUser = async (user: User) => {
    // Validate role assignment permissions
    if (user.role && user.role !== "Select" && currentUserRole) {
      if (!canAssignRole(currentUserRole, user.role)) {
        showToast("You don't have permission to assign this role", "error");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const token = await getValidToken();
      if (editUser) {
        // Update existing user
        const response = await updateUser(user, token, companyId!);
        if (response) {
          setIsSubmitting(false);
          onSave();
          showToast("User updated successfully", "success");
        } else {
          showToast("Failed to update user", "error");
          setIsSubmitting(false);
        }
      } else {
        const response = await addUser(
          { ...user, companyId: companyId },
          token
        );

        if (response) {
          setIsSubmitting(false);
          onSave();
          showToast("User added successfully", "success");
        } else {
          showToast("Something went wrong!", "error");
          setIsSubmitting(false);
        }
      }
    } catch (error: any) {
      console.error("Error saving user:", error);
      showToast(error?.message || "Something went wrong!", "error");
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editUser ? "Edit User" : "Add New User"}</DialogTitle>
      <Formik
        initialValues={editUser || initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSaveUser(values);
        }}
        enableReinitialize
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Name"
                    name="name"
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Phone"
                    name="phoneNumber"
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                    helperText={touched.phoneNumber && errors.phoneNumber}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Email (Optional)"
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    {...commonTextFieldProps}
                  />
                </Grid>

                {userScope === UserScope.GLOBAL && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Role"
                      name="role"
                      fullWidth
                      value={values.role}
                      error={touched.role && Boolean(errors.role)}
                      helperText={touched.role && errors.role}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("role", e.target.value);
                      }}
                      disabled={
                        !currentUserRole ||
                        getAvailableRoles(currentUserRole).length === 0
                      }
                      {...commonTextFieldProps}
                    >
                      <MenuItem
                        value="Select"
                        disabled
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Select
                      </MenuItem>
                      {currentUserRole &&
                        getAvailableRoles(currentUserRole).map((role) => (
                          <MenuItem
                            key={role}
                            value={role}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {role}
                          </MenuItem>
                        ))}
                    </TextField>
                    {(!currentUserRole ||
                      getAvailableRoles(currentUserRole).length === 0) && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {!currentUserRole
                          ? "Unable to determine your role. Please contact administrator."
                          : "You don't have permission to assign roles."}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            {/* show permission summary here based on the role */}
            {userScope === UserScope.GLOBAL &&
              permissionSummary &&
              values.role &&
              values.role !== "Select" && (
                <Box
                  style={{
                    margin: "16px",
                    padding: "12px",
                    background: "#f5f5f5",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: "1rem" }}
                  >
                    Permissions for {formatText(values.role)}:
                  </Typography>
                  <Box style={{ marginTop: "8px", fontSize: "0.8rem" }}>
                    {permissionSummary[values.role] ||
                      "No summary available for this role."}
                  </Box>
                </Box>
              )}
            <DialogActions>
              <Button
                onClick={onClose}
                {...commonButtonProps}
                color="error"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                {...commonButtonProps}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : editUser ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddUserModal;
